import { BRAND_HEX } from '../../styles/brand-colors';
import type { ToastVariantProps } from './Toast.variants';

type Size = NonNullable<ToastVariantProps['size']>;
type Variant = NonNullable<ToastVariantProps['variant']>;

/** Leading-icon px size per toast size (Figma: sm 22px, md 24px). */
export const TOAST_LEADING_ICON_SIZE: Record<Size, number> = { sm: 22, md: 24 };

/** Dismiss-X px size per toast size (Figma: sm 18px, md 20px). */
export const TOAST_DISMISS_ICON_SIZE: Record<Size, number> = { sm: 18, md: 20 };

/**
 * Resolved leading-icon colour per (variant, dark-mode). Resolved to a hex
 * string on BOTH platforms (not `currentColor`): the toast's leading icon,
 * title, description, and dismiss X are four different colours within one
 * variant, so a single container `text-*` can't drive them all — and the icons
 * are static (no hover/press colour shift) so the Button `currentColor` trick
 * earns nothing here. `lucide-react(-native)` / `react-native-svg` accept a
 * hex `color` prop directly.
 *
 * Light shades are read from each variant's leading-icon node via the Figma MCP
 * (file YZK9r3wDhvw1pY60KlVEIj) — all three are the 800 shade (= the description
 * tint), NOT the 900 title shade:
 *   default → secondary-800 (#262626) [1878:24938]
 *   critical → red-800     (#9f0712)  [1878:24959]
 *   success → green-800    (#016630)  [1878:24994]
 * Dark shades are the palette's palindromic inversion of the SAME 800 token
 * (800↔200), so they equal the light-palette 200 hex — verified against the
 * Figma dark nodes (2787:37155): dark leading icon = secondary/red/green-200.
 */
const LEADING_COLOR: Record<Variant, { light: string; dark: string }> = {
  default: { light: BRAND_HEX.secondary['800'], dark: BRAND_HEX.secondary['200'] },
  critical: { light: BRAND_HEX.red['800'], dark: BRAND_HEX.red['200'] },
  success: { light: BRAND_HEX.green['800'], dark: BRAND_HEX.green['200'] },
  // The conversational toast renders no leading status icon — this entry only
  // keeps the Record exhaustive. Neutral secondary, matching `default`.
  conversational: { light: BRAND_HEX.secondary['800'], dark: BRAND_HEX.secondary['200'] },
};

/**
 * Resolved dismiss-X colour per (variant, dark-mode). The X tint is
 * PER-VARIANT in Figma (read from each variant's `x` node via the Figma MCP,
 * file YZK9r3wDhvw1pY60KlVEIj):
 *   - default  → secondary-400 (#a1a1a1)  [node 1878:24943]
 *   - critical → red-800       (#9f0712)  [node 1878:24964]
 *   - success  → green-800     (#016630)  [node 1878:24999]
 * (Earlier this defaulted ALL three to the default's grey, which made the X
 * read as "no colour" on the coloured cards — the reported critical bug.)
 * Dark shades are the palette's palindromic inversion of each light token
 * (400↔600, 800↔200) — verified against the Figma dark nodes (2787:37155):
 * default X = secondary-600, critical X = red-200, success X = green-200.
 */
const DISMISS_COLOR: Record<Variant, { light: string; dark: string }> = {
  default: { light: BRAND_HEX.secondary['400'], dark: BRAND_HEX.secondary['600'] },
  critical: { light: BRAND_HEX.red['800'], dark: BRAND_HEX.red['200'] },
  success: { light: BRAND_HEX.green['800'], dark: BRAND_HEX.green['200'] },
  // Conversational toasts aren't dismissable — neutral placeholder for the Record.
  conversational: { light: BRAND_HEX.secondary['400'], dark: BRAND_HEX.secondary['600'] },
};

export function resolveToastIconColor(variant: Variant, isDark: boolean): string {
  const pair = LEADING_COLOR[variant];
  return isDark ? pair.dark : pair.light;
}

export function resolveToastDismissColor(variant: Variant, isDark: boolean): string {
  const pair = DISMISS_COLOR[variant];
  return isDark ? pair.dark : pair.light;
}
